import { useEffect } from 'react';
import usePostMessage, { type RemoteAction } from '@rio-cloud/rio-uikit/usePostMessage';

import {
    entitySelected,
    externalRefetchTriggerChanged,
    runsInLivemonitor,
    useEntitySourceId,
} from '../notifications/notificationSlice';
import { useAppDispatch } from '../../configuration/setup/hooks';
import { useFetchedNotifications } from '../notifications/notificationSlice';

const REMOTE_EVENT_ASSET_SELECTED = 'EVENT_SELECT_ASSET';
const REMOTE_EVENT_WIDGET_LOADED = 'EVENT_WIDGET_LOADED';
const REMOTE_EVENT_ASSET_NOTIFICATIONS_CHANGED = 'EVENT_ASSET_NOTIFICATIONS_CHANGED';

const EVENT_PLATFORM_NOTIFICATIONS_CHANGED = 'EVENT_PLATFORM_NOTIFICATIONS_CHANGED';
const EVENT_SEND_TO_WIDGETS = 'EVENT_SEND_TO_WIDGETS';

export const useRemoteMessage = (isVehicleMode: boolean) => {
    const dispatch = useAppDispatch();

    const vehicleId = useEntitySourceId();

    const notifications = useFetchedNotifications();

    const { sendMessageToParent } = usePostMessage('', (remoteAction: RemoteAction) => {
        if (remoteAction.type === REMOTE_EVENT_ASSET_SELECTED) {
            if (isVehicleMode) {
                // Dispatched only when in vehicleMode in Livemonitor sidebar to avoid resetting the notifications
                // for the global notifications in the header
                console.debug('change remote vehicle id', remoteAction.payload.id);
                dispatch(entitySelected(remoteAction.payload.id));
            }

            // receiving the asset message from the Livemonitor tells us we are running in that environment
            // where the notification link should behave differently
            dispatch(runsInLivemonitor());
        }

        // When this application runs inside the livemonitor and the vehicle notification read status changes
        // an event is triggered to the livemonitor parent and forwarded to the platform notification component in the header
        if (remoteAction.type === EVENT_SEND_TO_WIDGETS && remoteAction.payload.refetchPlatformNotifications) {
            // refetch notifications
            dispatch(externalRefetchTriggerChanged());
        }
    });

    // Only trigger when changing read status for a single or all notifications
    // to reflect the change in the Livemonitor immediately
    const notifyParentOnChange = () => {
        sendMessageToParent({
            type: EVENT_PLATFORM_NOTIFICATIONS_CHANGED,
            payload: {
                assetId: vehicleId,
            },
        });

        // When there is a vehicleId, we need to send an event via the Livemonitor to the
        // platform notification widget in the application header to trigger an update
        if (vehicleId) {
            sendMessageToParent({
                type: EVENT_SEND_TO_WIDGETS,
                payload: {
                    refetchPlatformNotifications: true,
                },
            });
        }
    };

    // Notify the livemonitor about new updates on vehicle-specific notification after polling them.
    // This is needed so the livemonitor can update the asset notification counter accordingly.
    useEffect(() => {
        if (isVehicleMode && vehicleId) {
            sendMessageToParent({
                type: REMOTE_EVENT_ASSET_NOTIFICATIONS_CHANGED,
                payload: { assetId: vehicleId },
            } as RemoteAction);
        }
    }, [vehicleId, notifications, isVehicleMode]);

    // When the application is loaded, send a message to the parent window to notify that the widget is loaded
    // and to receive the initial state data like if there is a vehicle selected or not. This information is
    // received as individual events, means the asset selected event handler above will receive the right asset id
    // on load
    useEffect(() => sendMessageToParent({ type: REMOTE_EVENT_WIDGET_LOADED } as RemoteAction), []);

    return {
        vehicleId,
        sendMessageToParent,
        notifyParentOnChange,
    };
};
